import { useMemo } from "react";
import {
  formatMDY,
  formatMDYWithTime,
  getStatusColor,
} from "@utils/ptoHelpers";
import { useTranslation } from "react-i18next";

interface PTOListMobileProps {
  requests: any[];
  searchVal?: string;
  onRequestClick: (request: any) => void;
}

export function PTOListMobile({
  requests,
  searchVal = "",
  onRequestClick,
}: PTOListMobileProps) {
  const { t } = useTranslation();

  const filteredRequests = useMemo(() => {
    if (!searchVal) return requests;
    const lowerSearch = searchVal.toLowerCase();
    return requests.filter((req) => {
      const searchableText = [
        req.description,
        req.status,
        req.request_type,
        req.status_reason,
        formatMDY(req.start_date),
        formatMDY(req.end_date),
        formatMDYWithTime(req.request_date),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(lowerSearch);
    });
  }, [requests, searchVal]);

  if (filteredRequests.length === 0) {
    return (
      <div className="tw-p-4 tw-text-center tw-text-gray-500">
        {searchVal ? t("pto.noMatchingRequests") : t("pto.noRequests")}
      </div>
    );
  }

  return (
    <div className="tw-space-y-3">
      {filteredRequests.map((request) => {
        const status = request.status;
        const statusKey = status?.toLowerCase() || "";
        const translatedStatus = t(`pto.statuses.${statusKey}`, {
          defaultValue: status,
        });

        return (
          <div
            key={request.id}
            className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow tw-cursor-pointer"
            onClick={() => onRequestClick(request)}
          >
            <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
              <div className="tw-flex-1">
                <div className="tw-text-sm tw-font-medium tw-text-gray-900">
                  {formatMDY(request.start_date)} -{" "}
                  {formatMDY(request.end_date)}
                </div>
                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  {t("pto.requestedDate")}:{" "}
                  {formatMDYWithTime(request.request_date)}
                </div>
              </div>
              <div
                className={`tw-text-sm tw-capitalize ${getStatusColor(
                  status,
                  request.request_type
                )}`}
              >
                {translatedStatus}
              </div>
            </div>

            <div className="tw-space-y-1 tw-text-sm">
              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-600">{t("pto.hours")}:</span>
                <span className="tw-font-medium">
                  {request.hours_requested || 0}
                </span>
              </div>

              {request.request_type && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">
                    {t("pto.requestType")}:
                  </span>
                  <span className="tw-capitalize">{request.request_type}</span>
                </div>
              )}

              {request.description && (
                <div className="tw-mt-2">
                  <span className="tw-text-gray-600">
                    {t("pto.description")}:
                  </span>
                  <p className="tw-text-gray-800 tw-mt-1">
                    {request.description}
                  </p>
                </div>
              )}

              {request.status_reason && (
                <div className="tw-mt-2">
                  <span className="tw-text-gray-600">
                    {t("pto.adminNote")}:
                  </span>
                  <p className="tw-text-gray-800 tw-mt-1">
                    {request.status_reason}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
